import { isDefined, Keys } from '@eih-netbroker/common';
import { useMemo } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { ErrorWidgetContent } from '../../../../shared/components/error-widget-content/ErrorWidgetContent';
import { FeedbackBox } from '../../../../shared/components/feedback-box/FeedbackBox';
import { ModalFooter, ModalFooterButtonProp } from '../../../../shared/components/modal/ModalFooter';
import { ModalHeader } from '../../../../shared/components/modal/ModalHeader';
import { usePayment } from '../../services/usePayment';
import { CashActionStep } from '../../states/cashActionAtoms';
import classes from './CashActionReceivedMessages.module.scss';

type CashActionReceivedMessagesProps = {
  title: string;
  onClose: () => void;
};

export const CashActionReceivedMessages = ({ title, onClose }: CashActionReceivedMessagesProps) => {
  const { translation } = useLocale();
  const { receivedMessages, getSubmitErrors, setCashActionStep, tempId, handleTwoFactorRestart } = usePayment();

  const processedMessages = useMemo(() => getSubmitErrors(receivedMessages), [receivedMessages, getSubmitErrors]);

  const rightButtonProps = useMemo<ModalFooterButtonProp>(() => {
    // Expired SMS code, restart process
    if (processedMessages?.details.some(m => m === Keys.CashAction.Errors.EXPIRED_SMS_MSG)) {
      return {
        action: handleTwoFactorRestart,
        title: translation.general.back,
      };
    }

    if (processedMessages?.type === 'error') {
      // Error received, abort process
      return {
        action: onClose,
        title: translation.general.cancel,
      };
    }

    // No errors, 2FA available
    if (isDefined(tempId)) {
      return {
        action: () => setCashActionStep(CashActionStep.Confirm),
        title: translation.general.continue,
      };
    }

    return {
      action: onClose,
      title: translation.general.ok,
    };
  }, [onClose, processedMessages, setCashActionStep, tempId, translation, handleTwoFactorRestart]);

  if (!isDefined(processedMessages)) {
    return (
      <div className={classes.container}>
        <ModalHeader
          title={title}
          onClose={onClose}
          isSmall
          isWithDivider
          className={classes.header}
        />

        <ErrorWidgetContent hideReload />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <ModalHeader
        title={title}
        onClose={onClose}
        isSmall
        isWithDivider
        className={classes.header}
      />
      <div className={classes.content}>
        <FeedbackBox
          type={processedMessages.type}
          title={`${
            processedMessages.type === 'error' ? translation.general.error.title : translation.general.attention
          } (${processedMessages.details.length})`}
          details={processedMessages.details.length > 1 ? processedMessages.details : undefined}
          message={processedMessages.details.length === 1 ? processedMessages.details[0] : undefined}
        />
      </div>

      <div className={classes.divider} />

      <ModalFooter right={rightButtonProps} />
    </div>
  );
};
